/**
 * Public Itinerary Share — branded landing for the URL we send customers
 * over WhatsApp: `<share-domain>/connection/itinerary/<customer-name>-<token>`.
 *
 * NO AUTH. Renders the PDF inline page-by-page via react-pdf (pdf.js) so
 * customers see the itinerary instantly without any download — aligned
 * with Pollen Dots' "reduce e-waste" stance. The Download button is
 * deliberately a quiet secondary affordance.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Download, Loader2 } from 'lucide-react';
import { API } from '@/App';

// Pin pdf.js worker to a CDN matching the installed pdfjs-dist version —
// same approach as DocumentViewer in the operator dashboard.
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const PublicItineraryShare = () => {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | missing
  const [blobUrl, setBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(900);
  const containerRef = useRef(null);

  // Resolve the API base. `API` from @/App is `${BACKEND_URL}/api` — strip
  // the trailing `/api` to compose absolute static URLs.
  const API_HOST = useMemo(() => API.replace(/\/api$/, ''), []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/public/itinerary/${slug}`);
        if (!res.ok) { if (live) setStatus('missing'); return; }
        const data = await res.json();
        if (live) { setMeta(data); setStatus('ok'); }
      } catch (e) {
        if (live) setStatus('missing');
      }
    })();
    return () => { live = false; };
  }, [slug]);

  // Pull the PDF as a Blob ONCE (no auth needed). react-pdf is happiest
  // rendering a blob URL because it streams chunks without re-downloading
  // each page. Also lets us share the same fetch across all pages.
  useEffect(() => {
    if (status !== 'ok' || !meta?.pdf_url) return;
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await fetch(`${API_HOST}${meta.pdf_url}`);
        if (!res.ok) throw new Error('PDF fetch failed');
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        const blob = new Blob([buf], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (e) {
        if (!cancelled) setStatus('missing');
      }
    })();
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [status, meta, API_HOST]);

  // Responsive page width — pdf.js renders to a canvas at this width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width || 900;
      setWidth(Math.min(1100, Math.max(320, Math.floor(w - 16))));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [status]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdf6e6] text-[#3f2e3e]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading itinerary…
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fdf6e6] text-[#3f2e3e] p-6 text-center">
        <div className="text-xl font-semibold mb-2">This itinerary is no longer available.</div>
        <p className="text-sm text-[#6b5566] max-w-xs">
          If you were expecting it, please get in touch with your Pollen Dots travel curator.
        </p>
        <a href="https://www.pollendots.com" className="mt-6 text-[#b16e32] font-semibold border-b border-[#b16e3266] pb-0.5">
          Visit Pollen Dots →
        </a>
      </div>
    );
  }

  const downloadHref = `${API_HOST}${meta.pdf_url}`;

  return (
    <div className="min-h-screen flex flex-col bg-[#fdf6e6]" data-testid="public-itinerary-page">
      {/* Branded top bar — mirrors the pollendots.com website header.
          Logo on the left with the tagline directly underneath, not beside. */}
      <div className="sticky top-0 z-10 flex items-center px-4 py-2.5 bg-[#fdf6e6] border-b border-[#e9d8b9]">
        <div className="flex flex-col items-start leading-tight">
          <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-7 sm:h-8 w-auto" data-testid="public-itinerary-logo" />
          <div className="text-[10px] sm:text-[11px] text-[#7a5a48] mt-0.5 tracking-wide ml-[34px] sm:ml-[40px]">Travel · Connect · Care</div>
        </div>
        <div className="flex-1" />
        {/* Quiet, secondary Download CTA — most customers won't need it. */}
        <a
          href={downloadHref}
          download={meta.filename}
          className="inline-flex items-center gap-1 text-xs text-[#7a5a48] hover:text-[#b16e32] px-2 py-1 rounded-full hover:bg-[#b16e3211] transition"
          data-testid="public-itinerary-download"
          title="Download (optional)"
        >
          <Download className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Download</span>
        </a>
      </div>

      {/* Inline PDF rendering — every page rendered as a canvas via pdf.js.
          Works on every modern mobile + desktop browser, including Android
          Chrome where native iframe PDF rendering is disabled. */}
      <div ref={containerRef} className="flex-1 w-full px-2 sm:px-4 py-4" data-testid="public-itinerary-pdf">
        {!blobUrl ? (
          <div className="flex items-center justify-center py-24 text-sm text-[#6b5566]">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading itinerary…
          </div>
        ) : (
          <Document
            file={blobUrl}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<div className="flex items-center justify-center py-24 text-sm text-[#6b5566]"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading itinerary…</div>}
            error={<div className="py-10 text-center text-sm text-[#a14545]">Couldn&apos;t render the PDF. Use the Download button to view.</div>}
          >
            <div className="flex flex-col items-center gap-3">
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i} className="bg-white shadow-md rounded border border-[#e9d8b9] overflow-hidden">
                  <Page
                    pageNumber={i + 1}
                    width={width}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </div>
              ))}
            </div>
          </Document>
        )}
      </div>

      {/* Bottom brand strip */}
      <div className="text-center text-[10px] text-[#7a5a48] py-3 border-t border-[#e9d8b9]/60">
        Crafted with care by <span className="font-semibold text-[#b16e32]">Pollen Dots</span> · Travel · Connect · Care
      </div>
    </div>
  );
};

export default PublicItineraryShare;
